// Firebase SDK initialization.
// Replace the placeholder values below with your project's real config (from
// Firebase console → Project Settings → General → Your apps → Web app).
//
// IMPORTANT: this config is PUBLIC. It does not contain secrets — security is
// enforced by Firestore/Storage rules and custom claims, not by hiding this object.

import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getMessaging, isSupported as isMessagingSupported } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

// ---- App Check (Task #62) ----
// GUARDED: only initializes when VITE_FIREBASE_APPCHECK_KEY (a reCAPTCHA v3 site
// key) is present. With no key set, this is a complete no-op — the app behaves
// exactly as before, so it's safe to ship before App Check is registered/enforced.
//
// Rollout: (1) register the web app for App Check with reCAPTCHA v3 in the
// Firebase console and paste the site key into .env.local; (2) deploy and watch
// the App Check metrics in UNENFORCED mode; (3) only after legit traffic shows
// as verified, turn on enforcement per service (Functions / Firestore / Storage).
//
// Local dev: set VITE_APPCHECK_DEBUG_TOKEN=true (or a registered debug token
// string) to use a debug token instead of real attestation.
const appCheckKey = import.meta.env.VITE_FIREBASE_APPCHECK_KEY;
if (appCheckKey) {
  const debugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
  if (debugToken) {
    // eslint-disable-next-line no-undef
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken === 'true' ? true : debugToken;
  }
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(appCheckKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    // Never let App Check init break app boot.
    console.warn('App Check init failed (continuing without it):', err?.message || err);
  }
}

export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, 'australia-southeast1');
export const storage = getStorage(app);

// FCM messaging is only available in browsers that support service workers + push.
// `isSupported` is async; consumers should await `getMessagingIfSupported()` before use.
export async function getMessagingIfSupported() {
  try {
    if (!(await isMessagingSupported())) return null;
    return getMessaging(app);
  } catch {
    return null;
  }
}

// Optional: hook into the local Firebase emulator suite during development.
// Set VITE_USE_EMULATORS=true in a .env.local file when developing.
if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectFunctionsEmulator(functions, 'localhost', 5001);
  connectStorageEmulator(storage, 'localhost', 9199);
}

export default app;
